// FT-184 REQ-12 / REQ-16 / write-layer §10: хранилище меток «через агента» — без сети.
//
// Метки едут в основном ответе экрана (решение USER 2026-10-05, правило проекта «одна страница —
// один запрос»): ключ agent_marks (AgentTools::Write::Marks на сервере)
//   { "<RecordType>" => { "<id строкой>" => { operation_id, tool, created_at } } }
// — только помеченные записи; для запрошенного типа без меток — пустой объект; не-сотруднику — {}.
// Источники:
//   GET /api/v1/booking_cards/:id     → data.card.agent_marks: Booking (бронь), Count (платежи карточки);
//   GET /api/v1/car_card_data/:id     → data.agent_marks: Car;
//   GET /api/v1/client_card/:id       → data.agent_marks: Client;
//   GET /api/v1/user_cashbox          → data.agent_marks: Count (журнал своей кассы);
//   GET /api/v1/company_counts_v2     → data.agent_marks: Count (журнал кассы компании);
//   GET /api/v1/crm/leads/:id/detail  → data.agent_marks: Lead;
//   GET /api/v1/crm/leads/:id         → data.meta.agent_marks: Lead.
//
// Экран, получив ответ, кладёт метки сюда (prime); компонент AgentMarker читает их (markerFor).
// Своих запросов у хранилища нет: метка появляется вместе с данными экрана и не может прийти
// «после» выхода из сессии — reset (store: unsetCurrentUser) очищает всё, и вернуть метки
// прежнего пользователя нечему.
import Vue from "vue";

// Источник сообщения CRM, отправленного агентом (Operations::Actor::AGENT_SOURCE): metadata.source.
export const AGENT_SOURCE = "mcp";
// Комментарий аудита правки агента (AgentTools::Write::Marker::AUDIT_PREFIX): "mcp:<operation_id>".
export const AUDIT_PREFIX = `${AGENT_SOURCE}:`;

export function isAgentMessage(message) {
  return !!(message && message.metadata && message.metadata.source === AGENT_SOURCE);
}

export function isAgentAuditComment(comment) {
  return typeof comment === "string" && comment.startsWith(AUDIT_PREFIX);
}

const isPlainObject = (value) => !!value && typeof value === "object" && !Array.isArray(value);

export function createAgentMarkerLoader() {
  // key "<type>:<id>" → { operation_id, tool, created_at } — реактивно: метки перерисуются по prime.
  const state = Vue.observable({ entries: {} });

  const keyOf = (type, id) => `${type}:${id}`;

  // Слить метки из ответа экрана. Ответ несёт только помеченные записи, поэтому метки сливаются,
  // а не заменяются: свежий ответ одного экрана не гасит метки другого. Мусор (null, не объект) —
  // не метки: экран без ключа agent_marks (старый API, не-сотрудник) просто ничего не добавляет.
  function prime(agentMarks) {
    if (!isPlainObject(agentMarks)) return;
    Object.keys(agentMarks).forEach((type) => {
      const byId = agentMarks[type];
      if (!isPlainObject(byId)) return;
      Object.keys(byId).forEach((id) => {
        const numericId = Number(id);
        if (!Number.isInteger(numericId) || numericId <= 0 || !isPlainObject(byId[id])) return;
        Vue.set(state.entries, keyOf(type, numericId), byId[id]);
      });
    });
  }

  function markerFor(type, id) {
    if (!type || id === null || id === undefined) return null;
    return state.entries[keyOf(type, Number(id))] || null;
  }

  // Выход из сессии (store: unsetCurrentUser): метки прежнего пользователя и группы не переживают logout.
  function reset() {
    Object.keys(state.entries).forEach((key) => Vue.delete(state.entries, key));
  }

  return { prime, markerFor, reset, state };
}

// Общее хранилище приложения: все экраны кладут метки в одно место.
export default createAgentMarkerLoader();
